/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import centroid from '@turf/centroid';
import { featureCollection } from 'shared-ui/maps/turfUtils';

import { GIS_SETUP_GEOCAMPSITES } from '../constants';

type Feature = {
    type: 'Feature';
    properties: {
        id: string;
        parent_campsite_id?: string;
        [key: string]: any;
    };
    geometry: any;
};

type FeatureCollection = {
    type: 'FeatureCollection';
    features: Feature[];
};

type State = {
    geoJsonCampsites: FeatureCollection | null;
    childrenByParent: Record<string, FeatureCollection> | null;
    geojsonCreatedParentCampsites: FeatureCollection | null;
    children?: Feature[];
};

type Action = {
    type: typeof GIS_SETUP_GEOCAMPSITES;
    payload: {
        features: Feature[];
        filteredFeatures: Feature[];
    };
};

const defaultState: State = {
    geoJsonCampsites: null,
    childrenByParent: null,
    geojsonCreatedParentCampsites: null,
};

const gisMapReducer = (state: State = defaultState, action: Action): State => {
    const setChildrenByParent = (sites: Feature[]): Record<string, FeatureCollection> => {
        const sitesByParentId = {};
        if (!sites || !sites.length) return sitesByParentId;

        const children = sites.filter((site) => site.properties.parent_campsite_id);
        if (!children || !children.length) return sitesByParentId;

        children.map((site) => {
            const parentId = site.properties.parent_campsite_id;
            if (!sitesByParentId[parentId]) {
                sitesByParentId[parentId] = {
                    type: 'FeatureCollection',
                    features: [site],
                };
            } else sitesByParentId[parentId].features.push(site);
            return site;
        });

        return sitesByParentId;
    };

    // Adds coordinates to parent campsites only if the campsite has children
    const addCoordinatesToParentCampsites = (
        campsites: Feature[],
        childrenByParent: Record<string, FeatureCollection>
    ) => {
        return campsites
            ?.filter((site) => childrenByParent[site.properties?.id]?.features?.length)
            .map((site) =>
                centroid(childrenByParent[site.properties.id], {
                    ...site.properties,
                })
            );
    };

    switch (action.type) {
        case GIS_SETUP_GEOCAMPSITES: {
            const childrenByParent = setChildrenByParent(action.payload.features);
            const parentCampsites = addCoordinatesToParentCampsites(
                action.payload.filteredFeatures,
                childrenByParent
            );
            const siteSpecificCampsites = action.payload.features?.filter(
                (sites) => !sites.properties?.parent_campsite_id
            );

            return {
                ...state,
                geoJsonCampsites: featureCollection(siteSpecificCampsites),
                geojsonCreatedParentCampsites: featureCollection(parentCampsites),
                children: action.payload.features?.filter(
                    (sites) => sites.properties.parent_campsite_id
                ),
                childrenByParent,
            };
        }
        default:
            return state;
    }
};

export default gisMapReducer;
